import { Injectable, inject } from '@angular/core';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { BehaviorSubject } from 'rxjs';
import { getUserRoles } from '../auth/keycloak-roles.util';

const COMPANY_ROLE = 'COMPANY';

export interface AccountSetupState {
  loading: boolean;
  required: boolean;
}

@Injectable({ providedIn: 'root' })
export class AccountSetupService {
  private readonly keycloak = inject(Keycloak);

  private readonly stateSubject = new BehaviorSubject<AccountSetupState>({
    loading: true,
    required: false
  });

  readonly setupRequired$ = this.stateSubject.asObservable();

  get isSetupRequired(): boolean {
    return this.stateSubject.value.required;
  }

  async check(): Promise<boolean> {
    await this.waitForKeycloakInitialization();

    const loggedIn = this.keycloak.authenticated ?? false;
    if (!loggedIn) {
      this.stateSubject.next({ loading: false, required: false });
      return false;
    }

    const roles = getUserRoles(this.keycloak);
    if (!roles.includes(COMPANY_ROLE)) {
      this.stateSubject.next({ loading: false, required: false });
      return false;
    }

    try {
      const profile: KeycloakProfile = await this.keycloak.loadUserProfile();
      const attributes = (profile as KeycloakProfile & { attributes?: Record<string, unknown> }).attributes;
      const required = attributes?.['marketingAgreement'] == null;
      this.stateSubject.next({ loading: false, required });
      return required;
    } catch {
      this.stateSubject.next({ loading: false, required: false });
      return false;
    }
  }

  private async waitForKeycloakInitialization(): Promise<void> {
    const timeoutMs = 10_000;
    const pollMs = 50;
    const startedAt = Date.now();

    while (!this.keycloak.didInitialize && Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
  }
}
